import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  CheckCircle,
  FileText,
  Package,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"
import {
  useDeclarationArticles,
  useDeclarationAudit,
} from "../../api/declarationsApi"
import { isPostApproval } from "../../api/lifecycle"
import type { Declaration } from "../../api/types"
import { intlLocale } from "../../utils/date"
import { holdReasonOf } from "../shared/holdReason"
import { McPill } from "../shared/McPill"
import { PatternPill } from "../shared/PatternPill"
import { RoundtripHoldPill } from "../shared/RoundtripHoldPill"
import { SectionLabel } from "../shared/SectionLabel"
import { DeclarationDeleteDialog } from "./DeclarationDeleteDialog"
import { DeclarationInlineDocumentPreview } from "./DeclarationInlineDocumentPreview"
import { DeclarationReextractDialog } from "./DeclarationReextractDialog"
import { declarationReviewPath } from "./routes"

interface DeclarationPanelProps {
  row: Declaration
  onClose: () => void
  /** Notified whenever the document section's expanded state changes, so the
   *  Sheet that mounts this panel (`DeclarationsModule`) can widen itself —
   *  the panel has no say over its own container's width. Omitted by tests
   *  that render the panel standalone. */
  onDocExpandedChange?: (expanded: boolean) => void
}

type TranslateFn = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

// A bare `YYYY-MM-DD` — `validUntil` and, on some rows, `capturedAt` — names a
// CALENDAR DATE, not an instant. `new Date("2027-08-01")` parses that as UTC
// midnight, which `toLocaleDateString` then reads back in the LOCAL zone: at
// any negative UTC offset (most of the Americas) that's the previous day,
// July 31st, rendered for an August 1st input. Splitting the fields and
// building the Date from local-time components (`new Date(y, m-1, d)`) makes
// the same calendar day the LOCAL midnight instead, so it survives the
// UTC→local read regardless of the viewer's offset.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

// Both `capturedAt`/`validUntil`/`performedAt` arrive as strings — the API
// client's case-conversion interceptor only revives `createdAt`/`updatedAt`
// into Date objects (see `caseConversions.ts`) — but not all of those strings
// are the same SHAPE: a full ISO timestamp (`performedAt`, always) already
// names an instant, so `new Date(v)` parses it correctly as-is; only a
// date-only string needs the local-component reconstruction above.
function toDate(v: unknown): Date | null {
  if (v == null || v === "") return null
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v
  const s = String(v)
  if (DATE_ONLY_RE.test(s)) {
    const [y, m, day] = s.split("-").map(Number)
    const d = new Date(y, m - 1, day)
    return isNaN(d.getTime()) ? null : d
  }
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}

// Same `dd.MM.yyyy` shape the list column uses (utils/date formatDate), but
// read from LOCAL components: that util reads UTC getters, which would undo
// the date-only fix above for any positive offset (local midnight Aug 1 is
// Jul 31 in UTC).
export function fmtDate(v: unknown): string {
  const d = toDate(v)
  if (!d) return "—"
  const dd = String(d.getDate()).padStart(2, "0")
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  return `${dd}.${mm}.${d.getFullYear()}`
}

// Activity timestamps carry a time-of-day the intake dates don't need — two
// audit events landing the same day still need to read as different moments.
function fmtDateTime(v: unknown, locale: string): string {
  const d = toDate(v)
  return d
    ? d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })
    : "—"
}

/** The framework label is a ", "-joined list of Verordnung names (same
 *  convention `DeclarationListV2`'s column splits on). */
function splitFrameworks(label: string | null | undefined): string[] {
  return (label ?? "")
    .split(", ")
    .map((x) => x.trim())
    .filter(Boolean)
}

/** Key above value, left-aligned: the panel's values (file names, several
 *  Verordnung names) are long, and right-aligned wrapping text read as a
 *  ragged block (product-owner screenshot, 28.08.). */
function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1 py-2.5">
      <span className="text-[11px] uppercase tracking-[0.06em] text-grey5">
        {label}
      </span>
      <div className="min-w-0 text-[12.5px] text-white">{children}</div>
    </div>
  )
}

export function getExpiryInfo(
  validUntil: string | Date | null | undefined,
  translate: TranslateFn,
) {
  if (!validUntil) return null
  if (typeof validUntil === "string" && validUntil.startsWith("—")) return null
  // `toDate` reads a date-only `YYYY-MM-DD` string as LOCAL midnight (same as
  // `fmtDate` above) — `new Date(validUntil)` would parse it as UTC midnight,
  // which is EARLIER than local midnight at any positive (east-of-UTC)
  // offset, silently pulling the diff-to-today one day into the past.
  const end = toDate(validUntil)
  if (!end) return null
  const today = new Date()
  // Calendar-day ordinals, not a millisecond subtraction: two local
  // midnights either side of a DST transition are 23h or 25h apart, so
  // `Math.ceil(diffMs / 86_400_000)` is off by one on exactly those days.
  // Counting whole UTC calendar days elapsed since the epoch is immune to
  // the local clock jumping — it never touches the wall-clock offset.
  const dayOrdinal = (d: Date) =>
    Math.floor(
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000,
    )
  const diffDays = dayOrdinal(end) - dayOrdinal(today)
  if (diffDays < 0)
    return {
      label: translate("MC_DECL_RECORD_EXPIRED_AGO", {
        n: Math.abs(diffDays),
      }),
      danger: true,
    }
  if (diffDays <= 90)
    return {
      label: translate("MC_DECL_BADGE_EXPIRING", { n: diffDays }),
      danger: false,
    }
  return null
}

// Maps the backend's raw audit event_type (entity_type='declaration') to a
// translated label; falls back to Title Case for any enum value not covered
// here (pattern mirrors prettyEventLabel in shared/AuditTrail.tsx).
//
// Every case below is grepped, not guessed: `grep -rn "entity_type='declaration'"
// -A2 backend/turnus_ai/{services,pipelines,mc_tools}` (2026-08-26), read for the
// paired `event_type=` a few lines below each hit — see DeclarationPanel.test.tsx's
// own copy of that list for the full grep trail. `declaration_codes_pending_resolution`
// / `declaration_reprocess_requested` / `declaration_field_reviewed` were not found by
// that grep (no current writer) but are kept as forward-compatible labels rather than
// removed speculatively.
export function formatEventType(
  eventType: string,
  translate: TranslateFn,
): string {
  switch (eventType) {
    case "declaration_received":
      return translate("MC_MD_AUDIT_EVT_DECL_RECEIVED")
    case "declaration_processed":
      return translate("MC_MD_AUDIT_EVT_DECL_PROCESSED")
    case "declaration_processing_failed":
      return translate("MC_MD_AUDIT_EVT_DECL_PROCESSING_FAILED")
    case "declaration_extraction_held":
      return translate("MC_MD_AUDIT_EVT_DECL_EXTRACTION_HELD")
    case "declaration_codes_pending_resolution":
      return translate("MC_MD_AUDIT_EVT_DECL_CODES_PENDING")
    case "declaration_mappings_resolved":
      return translate("MC_MD_AUDIT_EVT_DECL_MAPPINGS_RESOLVED")
    case "declaration_reprocess_requested":
      return translate("MC_MD_AUDIT_EVT_DECL_REPROCESS_REQUESTED")
    case "declaration_retry_requested":
      return translate("MC_MD_AUDIT_EVT_DECL_RETRY_REQUESTED")
    case "declaration_field_reviewed":
      return translate("MC_MD_AUDIT_EVT_DECL_FIELD_REVIEWED")
    case "declaration_claims_accepted_all":
      return translate("MC_MD_AUDIT_EVT_DECL_CLAIMS_ACCEPTED_ALL")
    case "declaration_scope_updated":
      return translate("MC_MD_AUDIT_EVT_DECL_SCOPE_UPDATED")
    case "declaration_supplier_assigned":
      return translate("MC_MD_AUDIT_EVT_DECL_SUPPLIER_ASSIGNED")
    case "declaration_article_remapped":
      return translate("MC_MD_AUDIT_EVT_DECL_ARTICLE_REMAPPED")
    case "declaration_approved":
      return translate("MC_MD_AUDIT_EVT_DECL_APPROVED")
    case "declaration_staging_complete":
      return translate("MC_MD_AUDIT_EVT_DECL_STAGING_COMPLETE")
    case "declaration_override_recorded":
      return translate("MC_MD_AUDIT_EVT_DECL_OVERRIDE_RECORDED")
    case "declaration_override_retired":
      return translate("MC_MD_AUDIT_EVT_DECL_OVERRIDE_RETIRED")
    case "declaration_inquiry_sent":
      return translate("MC_MD_AUDIT_EVT_DECL_INQUIRY_SENT")
    case "declaration_inquiry_answered":
      return translate("MC_MD_AUDIT_EVT_DECL_INQUIRY_ANSWERED")
    case "declaration_edition_confirmed":
      return translate("MC_MD_AUDIT_EVT_DECL_EDITION_CONFIRMED")
    case "verify_state_saved":
      return translate("MC_MD_AUDIT_EVT_DECL_VERIFY_STATE_SAVED")
    case "declaration_deleted":
      return translate("MC_MD_AUDIT_EVT_DECL_DELETED")
    default:
      // Fallback: unknown/future enum value — not keyable, arbitrary backend data.
      // The sentence around it is translated; only the raw type name is not.
      return translate("MC_MD_AUDIT_EVT_UNKNOWN", { type: eventType })
  }
}

// Machine actors write `performed_by` with a `<namespace>:<slug>` prefix —
// `system:declaration-pipeline` / `system:declaration-staging` (the
// extraction/staging pipelines) and `email:auto-declaration` (the inbound
// email-to-declaration pipeline, backend/turnus_ai/pipelines/email_to_declaration.py).
// Both namespaces get the SAME "automatic" label: from a reviewer's seat
// "who did this" only ever needs to distinguish a person from turnus itself,
// never one internal pipeline from another. Prefix checks, not an exact-string
// allowlist, so a future slug under either namespace still reads as automatic
// instead of leaking the raw actor string.
const MACHINE_ACTOR_PREFIXES = ["system:", "email:"]

export function formatActor(
  performedBy: string,
  currentUserEmail: string | undefined,
  translate: TranslateFn,
): string {
  if (MACHINE_ACTOR_PREFIXES.some((prefix) => performedBy.startsWith(prefix))) {
    return translate("MC_DECL_RECORD_ACTOR_SYSTEM")
  }
  if (
    currentUserEmail &&
    performedBy.toLowerCase() === currentUserEmail.toLowerCase()
  ) {
    return translate("TEAM/YOU")
  }
  return performedBy
}

export function DeclarationPanel({
  row,
  onClose,
  onDocExpandedChange,
}: DeclarationPanelProps) {
  const { translate, currentLanguage } = useTranslation()
  const { user } = useUser()
  // /users/me carries the address in `username` (backend `User` has no email
  // field, see api/auth.py `username=payload['email']`); `email` on the
  // frontend type is never populated by that endpoint, so fall through to it.
  const currentUserEmail = user?.email ?? user?.username
  const { data: articles = [] } = useDeclarationArticles(row.id)
  const { data: auditEvents = [] } = useDeclarationAudit(row.id)

  const expiry = getExpiryInfo(row.validUntil, translate)
  const frameworks = splitFrameworks(row.framework)
  // An APPROVED declaration has written master data naming it as evidence — the
  // backend refuses its delete (409 on `approved_at`). "accepted", "expiring"
  // AND "expired" are all post-approval readings (derive_lifecycle_status only
  // reaches them once `approved_at` is set), so none of the three offers the
  // control.
  const canDelete = !isPostApproval(row.lifecycleStatus)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [confirmReextractOpen, setConfirmReextractOpen] = useState(false)
  const { hasPermission } = useUser()
  // TUR-1474: a finished run can be re-read from the document while nothing has
  // been approved yet (the backend also refuses once a reviewer decision exists).
  const canReextract =
    hasPermission(PERMISSIONS.MC_DECLARATIONS_RETRY) &&
    row.lifecycleStatus === "ready"
  const [articlesExpanded, setArticlesExpanded] = useState(false)
  const [auditExpanded, setAuditExpanded] = useState(false)
  const [docExpanded, setDocExpanded] = useState(false)

  function toggleDocExpanded() {
    const next = !docExpanded
    setDocExpanded(next)
    onDocExpandedChange?.(next)
  }

  const visibleArticles = articlesExpanded ? articles : articles.slice(0, 5)
  const visibleAuditEvents = auditExpanded
    ? auditEvents
    : auditEvents.slice(0, 3)

  return (
    <div className="flex flex-col gap-0">
      {/* Header */}
      <div className="border-b border-grey4 p-5">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <SectionLabel className="mb-1">
              {translate("MC_DECL_RECORD_SECTION_LABEL")}
            </SectionLabel>
            <h3 className="m-0 truncate text-[17px] font-medium tracking-[-0.3px] text-white">
              {row.supplier}
            </h3>
            <div className="mt-1 flex items-center gap-2 text-[11.5px] text-grey5">
              <span>
                {translate("MC_DECL_RECORD_CAPTURED")} {fmtDate(row.capturedAt)}
              </span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label={translate("CLOSE")}
            className="h-7 w-7 shrink-0 p-0 text-grey5 hover:text-white"
            onClick={onClose}
          >
            <X size={14} />
          </Button>
        </div>
        {/* `py-0.5`: breathing room so the 22px pills never sit flush against
            the row edge inside the scroll container. */}
        <div className="flex flex-wrap gap-1.5 py-0.5">
          <PatternPill pattern={row.pattern} />
          {/* TUR-1452 — this document is a returned copy of the org's OWN
              supplier-inquiry package, come back unsigned: the pipeline held
              it (processed, nothing staged to approve). */}
          {(() => {
            const hold = holdReasonOf(row)
            return hold && <RoundtripHoldPill reason={hold} />
          })()}
          {(() => {
            // Derive "processed" from lifecycleStatus when present; fall back
            // to the legacy handoff field for older rows.
            const isProcessed =
              row.lifecycleStatus !== undefined
                ? row.lifecycleStatus === "ready" ||
                  isPostApproval(row.lifecycleStatus)
                : row.handoff === "done"
            return isProcessed ? (
              <McPill tone="success" icon={CheckCircle}>
                {translate("MC_DECL_RECORD_PROCESSED_PILL")}
              </McPill>
            ) : (
              <McPill tone="muted" icon={Package}>
                {translate("MC_DECL_RECORD_STORED_PENDING_PILL")}
              </McPill>
            )
          })()}
        </div>
      </div>

      {/* Intake data */}
      <div className="border-b border-grey4">
        <div className="border-b border-grey4 px-5 py-2.5">
          <SectionLabel>
            {translate("MC_DECL_RECORD_DATA_SECTION")}
          </SectionLabel>
        </div>
        <div className="divide-y divide-grey4 px-5">
          <Field label={translate("MC_DECL_RECORD_DOCUMENT")}>
            <span className="break-all">{row.doc}</span>
          </Field>
          <Field label={translate("MC_DECL_RECORD_FRAMEWORK")}>
            {frameworks.length === 0 ? (
              <span className="text-grey5">
                {translate("MC_DECL_RECORD_NOT_SPECIFIED")}
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {frameworks.map((fw) => (
                  <McPill
                    key={fw}
                    tone="info"
                    className="h-auto whitespace-normal py-0.5 text-left leading-snug"
                  >
                    {fw}
                  </McPill>
                ))}
              </div>
            )}
          </Field>
          <Field label={translate("MC_DECL_RECORD_VALID_UNTIL")}>
            {toDate(row.validUntil) ? (
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span>{fmtDate(row.validUntil)}</span>
                  {expiry && (
                    <span
                      className={`text-[11px] font-medium ${expiry.danger ? "text-red1" : "text-yellow1"}`}
                    >
                      {expiry.label}
                    </span>
                  )}
                </div>
                {row.validUntilSource === "default" && (
                  <span className="text-[11px] text-grey5">
                    {translate("MC_DECL_VALID_UNTIL_DEFAULT_HINT")}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-grey5">
                {translate("MC_DECL_RECORD_NOT_SPECIFIED")}
              </span>
            )}
          </Field>
        </div>
      </div>

      {/* Document preview — collapsed by default; the bytes are only fetched
          once expanded (DeclarationInlineDocumentPreview is unmounted while
          collapsed, so toggling off also drops the fetch/blob URL). */}
      <div className="border-b border-grey4">
        <div className="flex items-center justify-between gap-2 border-b border-grey4 px-5 py-2.5">
          <SectionLabel>{translate("MC_DECL_RECORD_DOCUMENT")}</SectionLabel>
          <div className="flex items-center gap-2">
            {/* Always present, expanded or not — the escape hatch for a
                document this preview's 60vh box is too small for. */}
            <Link
              to={declarationReviewPath(row.id)}
              className="text-[11.5px] text-grey5 hover:text-white hover:underline"
            >
              {translate("MC_DECL_RECORD_OPEN_IN_STUDIO")}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className={`h-6 gap-1 px-2 text-[11.5px] ${docExpanded ? "text-grey5 hover:text-white" : "text-yellow1 hover:text-yellow1"}`}
              onClick={toggleDocExpanded}
            >
              {docExpanded
                ? translate("MC_DECL_RECORD_HIDE_DOCUMENT_BTN")
                : translate("MC_DECL_RECORD_SHOW_DOCUMENT_BTN")}
            </Button>
          </div>
        </div>
        {docExpanded && (
          <div className="p-3">
            <DeclarationInlineDocumentPreview declId={row.id} />
          </div>
        )}
      </div>

      {/* Articles covered */}
      <div className="border-b border-grey4">
        <div className="border-b border-grey4 px-5 py-2.5">
          <SectionLabel>
            {translate("MC_DECL_RECORD_ARTICLES_SECTION")}
            {articles.length > 0 && (
              <span className="ml-1.5 normal-case tracking-normal text-grey5">
                · {articles.length}
              </span>
            )}
          </SectionLabel>
        </div>
        {articles.length === 0 ? (
          <p className="px-5 py-3 text-[12px] text-grey5">
            {translate("MC_DECL_RECORD_ARTICLES_EMPTY")}
          </p>
        ) : (
          <>
            <ul>
              {visibleArticles.map((a) => (
                <li
                  key={a.articleNumber}
                  className="flex items-center gap-2.5 border-b border-grey4 px-5 py-2.5 last:border-0"
                >
                  <FileText size={12} className="shrink-0 text-grey5" />
                  <span className="font-mono text-[11px] text-grey5">
                    {a.articleNumber}
                  </span>
                  <span className="truncate text-[12px] text-white">
                    {a.name}
                  </span>
                </li>
              ))}
            </ul>
            {articles.length > 5 && (
              <button
                type="button"
                className="w-full px-5 py-2 text-left text-[11.5px] text-grey5 hover:text-white"
                onClick={() => setArticlesExpanded((v) => !v)}
              >
                {articlesExpanded
                  ? translate("MC_DECL_RECORD_SHOW_LESS")
                  : translate("MC_DECL_RECORD_SHOW_ALL", {
                      n: articles.length,
                    })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Recent activity */}
      <div className="border-b border-grey4">
        <div className="border-b border-grey4 px-5 py-2.5">
          <SectionLabel>
            {translate("MC_DECL_RECORD_ACTIVITY_SECTION")}
          </SectionLabel>
        </div>
        {auditEvents.length === 0 ? (
          <p className="px-5 py-3 text-[12px] text-grey5">
            {translate("MC_DECL_RECORD_ACTIVITY_EMPTY")}
          </p>
        ) : (
          <>
            <ul>
              {visibleAuditEvents.map((e) => (
                <li
                  key={e.id}
                  className="border-b border-grey4 px-5 py-2.5 last:border-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] text-white">
                      {formatEventType(e.eventType, translate)}
                    </span>
                    <span className="font-mono text-[11px] text-grey5">
                      {fmtDateTime(e.performedAt, intlLocale(currentLanguage))}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-[11.5px] text-grey5">
                    {formatActor(e.performedBy, currentUserEmail, translate)}
                  </div>
                </li>
              ))}
            </ul>
            {auditEvents.length > 3 && (
              <button
                type="button"
                className="w-full px-5 py-2 text-left text-[11.5px] text-grey5 hover:text-white"
                onClick={() => setAuditExpanded((v) => !v)}
              >
                {auditExpanded
                  ? translate("MC_DECL_RECORD_SHOW_LESS")
                  : translate("MC_DECL_RECORD_SHOW_ALL", {
                      n: auditEvents.length,
                    })}
              </button>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      {(canDelete || canReextract) && (
        <div className="flex flex-col gap-2 p-5">
          <SectionLabel className="mb-1">
            {translate("MC_DECL_RECORD_ACTIONS_SECTION")}
          </SectionLabel>
          {canReextract && (
            <Button
              variant="outline"
              size="sm"
              className="h-[30px] w-full gap-1.5 text-[12.5px]"
              onClick={() => setConfirmReextractOpen(true)}
            >
              <RefreshCw size={12} />
              {translate("MC_DECL_REEXTRACT_BTN")}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="h-[30px] w-full gap-1.5 border-red1/40 text-[12.5px] text-red1 hover:bg-red1/10 hover:text-red1"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <Trash2 size={12} />
              {translate("MC_DECL_DELETE_BTN")}
            </Button>
          )}
        </div>
      )}

      <DeclarationReextractDialog
        declarationId={confirmReextractOpen ? row.id : null}
        onClose={() => setConfirmReextractOpen(false)}
      />

      <DeclarationDeleteDialog
        declarationId={confirmDeleteOpen ? row.id : null}
        onClose={() => setConfirmDeleteOpen(false)}
        onDeleted={onClose}
      />
    </div>
  )
}
